import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Minus } from "lucide-react";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const STATE_LABEL = {
    all: "On all selected",
    mixed: "On some selected",
    none: "On none",
};

/**
 * Mixed-state lead-types bulk editor.
 *
 * Props:
 * - open / onOpenChange
 * - selectionPayload: `{ ids }` or `{ filter }` — passed to `/leads/bulk-type-state`
 *   and (after computing add/remove) to `/leads/bulk-update-types`.
 * - effectiveCount: shown in the modal copy + confirm summary.
 * - onApplied: callback after successful save.
 */
export default function BulkUpdateLeadTypesDialog({
    open, onOpenChange, selectionPayload, effectiveCount, onApplied,
}) {
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [rows, setRows] = useState([]);          // [{id,name,color,state,count}]
    const [overrides, setOverrides] = useState({}); // typeId -> "on" | "off"
    const [total, setTotal] = useState(0);

    useEffect(() => {
        if (!open || !selectionPayload) return;
        let alive = true;
        setOverrides({});
        setRows([]);
        setLoading(true);
        api.post("/leads/bulk-type-state", selectionPayload)
            .then(({ data }) => {
                if (!alive) return;
                setRows(data.states || []);
                setTotal(data.total || 0);
            })
            .catch(e => toast.error(formatApiError(e)))
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [open, selectionPayload]);

    const effectiveStateFor = (row) => overrides[row.id] || row.state;
    const changeRow = (row) => {
        const cur = effectiveStateFor(row);
        // Tri-state click: all -> none -> all; mixed becomes either "on" or "off" depending on click cycle.
        // We model the click as a flip: anything that is 'all' or 'on' becomes 'off'; anything else becomes 'on'.
        const next = (cur === "all" || cur === "on") ? "off" : "on";
        setOverrides(prev => ({ ...prev, [row.id]: next }));
    };
    const resetRow = (row) => {
        setOverrides(prev => {
            const n = { ...prev };
            delete n[row.id];
            return n;
        });
    };

    // Compute add/remove sets relative to the original state.
    const add = [];
    const remove = [];
    rows.forEach(row => {
        const o = overrides[row.id];
        if (!o) return; // unchanged
        if (o === "on" && row.state !== "all") add.push(row.id);
        if (o === "off" && row.state !== "none") remove.push(row.id);
    });

    const submit = async () => {
        if (!add.length && !remove.length) {
            toast.info("No changes to apply");
            return;
        }
        setBusy(true);
        try {
            const payload = { ...selectionPayload, add, remove };
            const { data } = await api.post("/leads/bulk-update-types", payload);
            toast.success(`Updated lead types on ${data.modified.toLocaleString()} of ${(effectiveCount || total).toLocaleString()} leads`);
            onOpenChange(false);
            setConfirmOpen(false);
            onApplied?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl" data-testid="bulk-update-types-dialog">
                <DialogHeader>
                    <DialogTitle>Update Lead Types — {(effectiveCount || total).toLocaleString()} leads</DialogTitle>
                    <DialogDescription className="leading-relaxed">
                        <span className="font-medium text-gray-900">Checked</span> means every selected lead has this type.{" "}
                        <span className="font-medium text-gray-900">Mixed</span> means only some do — leave it mixed to preserve existing assignments.{" "}
                        <span className="font-medium text-gray-900">Unchecked</span> means none do.
                    </DialogDescription>
                </DialogHeader>

                <div className="border border-border rounded-md max-h-80 overflow-auto" data-testid="bulk-update-types-list">
                    {loading ? (
                        <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
                    ) : rows.length === 0 ? (
                        <div className="p-8 text-center text-sm text-muted-foreground">No lead types defined.</div>
                    ) : (
                        <ul className="divide-y divide-border">
                            {rows.map(row => {
                                const s = effectiveStateFor(row);
                                const changed = !!overrides[row.id];
                                return (
                                    <li key={row.id} className="flex items-center gap-3 px-3 py-2" data-testid={`bulk-type-row-${row.id}`}>
                                        <button
                                            type="button"
                                            onClick={() => changeRow(row)}
                                            data-testid={`bulk-type-toggle-${row.id}`}
                                            aria-checked={s === "all" || s === "on"}
                                            className={`h-5 w-5 shrink-0 rounded border flex items-center justify-center transition-colors ${
                                                s === "all" || s === "on"
                                                    ? "bg-primary border-primary text-white"
                                                    : s === "mixed"
                                                        ? "bg-amber-50 border-amber-400 text-amber-700"
                                                        : "bg-white border-border text-transparent hover:border-primary"
                                            }`}
                                        >
                                            {s === "mixed" ? <Minus className="h-3 w-3" strokeWidth={3} /> : "✓"}
                                        </button>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span
                                                    className="inline-block h-2.5 w-2.5 rounded-full"
                                                    style={{ backgroundColor: row.color || "#94a3b8" }}
                                                />
                                                <span className="text-sm font-medium text-gray-900">{row.name}</span>
                                                <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{STATE_LABEL[row.state]}</span>
                                                {changed && (
                                                    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${s === "on" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                                                        {s === "on" ? "will be added" : "will be removed"}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-[11px] text-muted-foreground tabular">
                                                {row.count.toLocaleString()} of {total.toLocaleString()} currently have this type
                                            </div>
                                        </div>
                                        {changed && (
                                            <button
                                                type="button"
                                                onClick={() => resetRow(row)}
                                                data-testid={`bulk-type-reset-${row.id}`}
                                                className="text-[11px] text-muted-foreground hover:text-gray-700 underline"
                                            >
                                                Reset
                                            </button>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                <div className="text-xs text-muted-foreground bg-slate-50 border border-border rounded-md px-3 py-2 leading-relaxed">
                    Only the lead types you change will be applied. Mixed rows you don't touch stay as-is — each lead keeps its own current assignment.
                </div>

                <DialogFooter className="flex-row justify-between sm:justify-between gap-2">
                    <div className="text-xs text-muted-foreground self-center">
                        {add.length + remove.length === 0 ? "No changes yet" : `${add.length} to add · ${remove.length} to remove`}
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bulk-update-types-cancel">Cancel</Button>
                        <Button
                            onClick={() => setConfirmOpen(true)}
                            disabled={busy || (add.length + remove.length === 0)}
                            data-testid="bulk-update-types-apply"
                        >
                            Review & apply
                        </Button>
                    </div>
                </DialogFooter>

                <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                    <DialogContent className="max-w-md" data-testid="bulk-update-types-confirm">
                        <DialogHeader>
                            <DialogTitle>Confirm bulk update</DialogTitle>
                            <DialogDescription className="leading-relaxed pt-1">
                                You are updating lead types for <span className="font-semibold text-gray-900">{(effectiveCount || total).toLocaleString()}</span> leads.
                            </DialogDescription>
                        </DialogHeader>
                        <ul className="text-sm space-y-1">
                            {add.length > 0 && (
                                <li>
                                    <span className="text-emerald-700 font-medium">Add ({add.length}):</span>{" "}
                                    {add.map(id => rows.find(r => r.id === id)?.name).filter(Boolean).join(", ")}
                                </li>
                            )}
                            {remove.length > 0 && (
                                <li>
                                    <span className="text-rose-700 font-medium">Remove ({remove.length}):</span>{" "}
                                    {remove.map(id => rows.find(r => r.id === id)?.name).filter(Boolean).join(", ")}
                                </li>
                            )}
                        </ul>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="bulk-update-types-confirm-cancel">Back</Button>
                            <Button onClick={submit} disabled={busy} data-testid="bulk-update-types-confirm-apply">
                                {busy ? "Applying…" : "Apply changes"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </DialogContent>
        </Dialog>
    );
}
